import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, AlertTriangle, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate, COMMISSION_TX_TYPES, walletTxTypeLabel } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import ReferralTrackingPanel from "../../components/ReferralTrackingPanel";
import MilestoneProgressWidget from "../../components/MilestoneProgressWidget";
import MilestoneHistoryPanel from "../../components/MilestoneHistoryPanel";
import IncentiveCommissionBoxes from "../../components/IncentiveCommissionBoxes";

export default function Earnings() {
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [upi, setUpi] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/wallet"); setW(data); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  if (!w) return <div className="text-gray-500">Loading…</div>;

  const isBrokerAgent = !!user?.broker_admin_id; // agent registered under a broker
  const balance = w?.balance_paise || 0;
  const totalEarned = w?.total_earned_paise || 0;
  const totalWithdrawn = w?.total_withdrawn_paise || 0;

  // For broker-agent: commission only (exclude brokerage transactions)
  const commissionEarned = (w.transactions || [])
    .filter(t => COMMISSION_TX_TYPES.includes(t.type))
    .reduce((sum, t) => sum + (t.amount_paise || 0), 0);

  // Yearly savings = commission earned in last 365 days
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  const yearlyCommission = (w.transactions || [])
    .filter(t =>
      COMMISSION_TX_TYPES.includes(t.type) &&
      new Date(t.created_at) >= oneYearAgo
    )
    .reduce((sum, t) => sum + (t.amount_paise || 0), 0);

  // Brokerage earned (solo agents only)
  const brokerageEarned = (w.transactions || [])
    .filter(t => t.type === "brokerage")
    .reduce((sum, t) => sum + (t.amount_paise || 0), 0);

  const minWithdraw = 25000; // ₹250 in paise
  const canWithdraw = balance >= minWithdraw;

  const withdraw = async () => {
    if (user.kyc_status !== "verified") { toast.error("Verify your identity first."); return; }
    if (balance < minWithdraw) { toast.error("Minimum withdrawal is ₹250."); return; }
    setBusy(true);
    try {
      await api.post("/wallet/withdraw", { upi_id: upi, amount_paise: balance });
      toast.success(`${formatINR(balance)} sent to ${upi}!`);
      setUpi(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const typeLabel = walletTxTypeLabel;

  // Filter transactions based on role
  const visibleTransactions = isBrokerAgent
    ? (w.transactions || []).filter(t => t.type !== "brokerage")
    : (w.transactions || []);

  return (
    <div className="space-y-6" data-testid="earnings-page">
      <div>
        <h1 className="text-2xl font-bold font-display">My Earnings</h1>
        <p className="text-sm text-gray-500 mt-1">
          {isBrokerAgent
            ? "Your Orello commission from completed deals."
            : "Orello commission and brokerage from completed deals."}
        </p>
      </div>

      <IncentiveCommissionBoxes wallet={w} />

      {/* ── Stat cards ── */}
      {isBrokerAgent ? (
        // Agent under broker — show commission only, no brokerage
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card-base p-5">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Ready to Withdraw</div>
            <div className="mt-2 text-2xl font-bold font-display text-emerald-600">{formatINR(balance)}</div>
            <div className="text-xs text-gray-400 mt-1">Min. ₹250 to withdraw</div>
          </div>
          <div className="card-base p-5">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Commission Earned</div>
            <div className="mt-2 text-2xl font-bold font-display">{formatINR(commissionEarned)}</div>
            <div className="text-xs text-gray-400 mt-1">All time from Orello</div>
          </div>
          <div className="card-base p-5">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Already Paid Out</div>
            <div className="mt-2 text-2xl font-bold font-display text-gray-700">{formatINR(totalWithdrawn)}</div>
            <div className="text-xs text-gray-400 mt-1">Withdrawn to your UPI</div>
          </div>
          <div className="card-base p-5 bg-[#FFF4ED] border border-indigo-100">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#F97316]" />
              <div className="text-xs text-[#F97316] uppercase tracking-wide font-medium">Yearly Earnings</div>
            </div>
            <div className="mt-2 text-2xl font-bold font-display text-[#F97316]">{formatINR(yearlyCommission)}</div>
            <div className="text-xs text-gray-400 mt-1">Your earnings in the last 12 months</div>
          </div>
        </div>
      ) : (
        // Solo agent — show everything including brokerage
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="card-base p-5">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Ready to Withdraw</div>
            <div className="mt-2 text-2xl font-bold font-display text-emerald-600">{formatINR(balance)}</div>
            <div className="text-xs text-gray-400 mt-1">Min. ₹250 to withdraw</div>
          </div>
          <div className="card-base p-5">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Brokerage Earned</div>
            <div className="mt-2 text-2xl font-bold font-display">{formatINR(brokerageEarned)}</div>
            <div className="text-xs text-gray-400 mt-1">Dalali from all deals</div>
          </div>
          <div className="card-base p-5">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total Withdrawn</div>
            <div className="mt-2 text-2xl font-bold font-display">{formatINR(totalWithdrawn)}</div>
          </div>
          <div className="card-base p-5 bg-[#FFF4ED] border border-indigo-100">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[#F97316]" />
              <div className="text-xs text-[#F97316] uppercase tracking-wide font-medium">Yearly Earnings</div>
            </div>
            <div className="mt-2 text-2xl font-bold font-display text-[#F97316]">{formatINR(yearlyCommission)}</div>
            <div className="text-xs text-gray-400 mt-1">Your earnings in the last 12 months</div>
          </div>
        </div>
      )}

      {/* ── Withdraw section ── */}
      {!canWithdraw && (
        <div className="card-base p-5">
          <div className="text-sm text-gray-700">
            {formatINR(minWithdraw - balance)} more needed to unlock withdrawal
          </div>
          <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-2 bg-[#F97316] transition-all"
              style={{ width: `${Math.min(100, (balance / minWithdraw) * 100)}%` }} />
          </div>
          <div className="text-xs text-gray-500 mt-2">Minimum withdrawal is ₹250</div>
        </div>
      )}

      {canWithdraw && user.kyc_status !== "verified" && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <div className="font-medium">Verify your identity before withdrawing</div>
            <p className="text-sm text-gray-600 mt-1">Takes 2 minutes. One time only.</p>
            <Link to="/onboarding/kyc" className="inline-flex mt-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
              Verify Now →
            </Link>
          </div>
        </div>
      )}

      {canWithdraw && user.kyc_status === "verified" && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5" data-testid="withdraw-card">
          <h3 className="text-lg font-semibold font-display">Ready to withdraw!</h3>
          <p className="text-sm text-gray-600 mt-1">
            {formatINR(balance)} is ready. Enter your UPI ID to get paid instantly.
          </p>
          <input
            data-testid="upi-input"
            value={upi}
            onChange={(e) => setUpi(e.target.value)}
            placeholder="yourname@upi or phone@upi"
            className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">Works with PhonePe, GPay, Paytm, any UPI app</p>
          <button
            data-testid="withdraw-btn"
            onClick={withdraw}
            disabled={busy || !upi}
            className="mt-4 bg-emerald-500 hover:bg-emerald-600 btn-press text-white px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Wallet className="h-4 w-4" />
            {busy ? "Sending…" : `Withdraw ${formatINR(balance)}`}
          </button>
        </div>
      )}

      {/* ── Earnings history ── */}
      <div className="card-base">
        <div className="px-5 py-4 border-b border-gray-100 font-semibold">
          Earnings history
          {isBrokerAgent && (
            <span className="ml-2 text-xs text-gray-400 font-normal">
              Commission & referral only — brokerage recorded with your broker
            </span>
          )}
        </div>
        {visibleTransactions.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">
            No earnings yet.<br />Complete your first deal to start earning.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] text-gray-500 uppercase bg-gray-50">
              <tr>
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-5 py-3">Property / Detail</th>
                <th className="text-left px-5 py-3">Type</th>
                <th className="text-right px-5 py-3">Amount</th>
                <th className="text-left px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {visibleTransactions.map(t => (
                <tr key={t.id} className="border-t border-gray-100">
                  <td className="px-5 py-3 text-gray-600">{formatDate(t.created_at)}</td>
                  <td className="px-5 py-3">{t.property_address || t.description || "—"}</td>
                  <td className="px-5 py-3 text-gray-600">{typeLabel(t.type)}</td>
                  <td className={`px-5 py-3 text-right font-medium ${t.type === "withdrawal" ? "text-gray-600" : "text-emerald-600"}`}>
                    {t.type === "withdrawal" ? "-" : "+"}{formatINR(t.amount_paise)}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`pill ${t.status === "completed" ? "bg-emerald-50 text-emerald-700" : t.status === "failed" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
                      {t.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <MilestoneProgressWidget />

      <ReferralTrackingPanel />

      <MilestoneHistoryPanel />
    </div>
  );
}